import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Search, Pencil, Trash2, Upload, Download, TicketPlus, History } from "lucide-react";
import { JobHistory } from "@/components/customers/JobHistory";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useZipAutofill } from "@/hooks/useZipAutofill";

const empty = { first_name: "", last_name: "", email: "", phone: "", street: "", city: "", state: "", zip_code: "", county: "" };

export default function Customers() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [smsConsent, setSmsConsent] = useState(false);
  const [smsConsentSource, setSmsConsentSource] = useState("verbal");
  const [initialConsentAt, setInitialConsentAt] = useState(null);
  const [dupMatch, setDupMatch] = useState(null);

  useZipAutofill(
    form.zip_code,
    { city: form.city, state: form.state, county: form.county },
    (patch) => setForm((s) => ({ ...s, ...patch })),
  );
  const [importOpen, setImportOpen] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/customers", { params: search ? { search } : {} });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [search]);

  useEffect(() => {
    load();
    // Only run on mount — subsequent loads are triggered by the Search button.
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setSmsConsent(false);
    setSmsConsentSource("verbal");
    setInitialConsentAt(null);
    setDupMatch(null);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      first_name: c.first_name || "",
      last_name: c.last_name || "",
      email: c.email || "",
      phone: c.phone || "",
      street: c.street || "",
      city: c.city || "",
      state: c.state || "",
      zip_code: c.zip_code || "",
      county: c.county || "",
    });
    setSmsConsent(!!c.sms_consent_at);
    setSmsConsentSource(c.sms_consent_source || "verbal");
    setInitialConsentAt(c.sms_consent_at || null);
    setDupMatch(null);
    setOpen(true);
  };

  const save = async (skipDupCheck = false) => {
    if (!form.first_name || !form.last_name || !form.phone) {
      toast.error("First name, last name and phone are required");
      return;
    }
    try {
      if (!editing && !skipDupCheck) {
        const { data } = await api.get("/customers/check-duplicate", {
          params: { phone: form.phone, email: form.email || undefined },
        });
        if (data.match) {
          setDupMatch(data);
          return;
        }
      }
      setDupMatch(null);
      const payload = { ...form, email: form.email || null };
      let customerId = editing?.id;
      if (editing) {
        await api.put(`/customers/${editing.id}`, payload);
      } else {
        const { data } = await api.post("/customers", payload);
        customerId = data.id;
      }

      // Persist SMS consent state if it changed (or if it's a new customer with consent on)
      const wasConsented = !!initialConsentAt;
      if (customerId && smsConsent !== wasConsented) {
        await api.post(`/customers/${customerId}/sms-consent`, {
          consented: smsConsent,
          source: smsConsent ? smsConsentSource : undefined,
        });
      }

      toast.success(editing ? "Customer updated" : "Customer added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("Customer deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadTemplate = () => {
    const csv = "first_name,last_name,email,phone,street,city,state,zip_code\nJane,Doe,jane@example.com,555-0101,123 Main St,Austin,TX,78701\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "customers_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const runImport = async () => {
    if (!importFile) {
      toast.error("Choose a CSV file first");
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const res = await fetch(`${API}/customers/import`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(typeof data.detail === "string" ? data.detail : "Import failed");
      }
      setImportResult(data);
      toast.success(`Imported ${data.created} customer(s)`);
      load();
    } catch (e) {
      toast.error(e.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const exportCustomers = async () => {
    try {
      const res = await fetch(`${API}/exports/customers.csv`, { credentials: "include" });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const disp = res.headers.get("Content-Disposition") || "";
      const match = disp.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : "customers.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(e.message || "Export failed");
    }
  };

  const closeImport = () => {
    setImportOpen(false);
    setImportFile(null);
    setImportResult(null);
  };

  return (
    <div className="space-y-6" data-testid="customers-root">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Customers</h1>
          <p className="text-sm text-slate-500 mt-1">Your customer directory.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportCustomers} variant="outline" className="gap-2" data-testid="export-customers-btn">
            <Download className="w-4 h-4" /> Export CSV
          </Button>
          <Button onClick={() => setImportOpen(true)} variant="outline" className="gap-2" data-testid="import-customers-btn">
            <Upload className="w-4 h-4" /> Import CSV
          </Button>
          <Button onClick={openNew} className="bg-blue-700 hover:bg-blue-800 gap-2" data-testid="new-customer-btn">
            <Plus className="w-4 h-4" /> New Customer
          </Button>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <form onSubmit={onSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone, email…" className="pl-9" data-testid="customer-search" />
            </div>
            <Button type="submit" variant="outline">Search</Button>
          </form>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Address</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8" data-testid="empty-customers">No customers yet.</TableCell></TableRow>
              )}
              {items.map((c) => (
                <TableRow key={c.id} className="hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
                  <TableCell className="font-medium text-slate-900">{c.first_name} {c.last_name}</TableCell>
                  <TableCell>{c.phone}</TableCell>
                  <TableCell>{c.email || <span className="text-slate-400">—</span>}</TableCell>
                  <TableCell className="text-sm text-slate-600">
                    {[c.street, c.city, c.state, c.zip_code].filter(Boolean).join(", ") || <span className="text-slate-400">—</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button asChild size="sm" variant="outline" className="gap-1 text-blue-700 border-blue-200 hover:bg-blue-50" data-testid={`new-ticket-for-${c.id}`}>
                        <Link to={`/tickets/new?customer=${c.id}`}>
                          <TicketPlus className="w-3.5 h-3.5" /> New ticket
                        </Link>
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setHistoryFor(c)} className="gap-1" data-testid={`history-customer-${c.id}`}>
                        <History className="w-3.5 h-3.5" /> History
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(c)} className="gap-1" data-testid={`edit-customer-${c.id}`}>
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </Button>
                      {isAdmin && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" className="text-red-700 border-red-200 hover:bg-red-50 gap-1" data-testid={`delete-customer-${c.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete customer?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will also delete all their tickets. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(c)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit customer" : "New customer"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>First name *</Label>
              <Input value={form.first_name} onChange={(e) => setForm((s) => ({ ...s, first_name: e.target.value }))} data-testid="cust-first" />
            </div>
            <div className="space-y-2">
              <Label>Last name *</Label>
              <Input value={form.last_name} onChange={(e) => setForm((s) => ({ ...s, last_name: e.target.value }))} data-testid="cust-last" />
            </div>
            <div className="space-y-2">
              <Label>Phone *</Label>
              <Input value={form.phone} onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))} data-testid="cust-phone" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} data-testid="cust-email" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Street</Label>
              <Input value={form.street} onChange={(e) => setForm((s) => ({ ...s, street: e.target.value }))} data-testid="cust-street" />
            </div>
            <div className="space-y-2">
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => setForm((s) => ({ ...s, city: e.target.value }))} data-testid="cust-city" />
            </div>
            <div className="space-y-2">
              <Label>State</Label>
              <Input value={form.state} onChange={(e) => setForm((s) => ({ ...s, state: e.target.value }))} data-testid="cust-state" />
            </div>
            <div className="space-y-2">
              <Label>Zip code</Label>
              <Input value={form.zip_code} onChange={(e) => setForm((s) => ({ ...s, zip_code: e.target.value }))} data-testid="cust-zip" />
            </div>
            <div className="space-y-2">
              <Label>County</Label>
              <Input value={form.county} onChange={(e) => setForm((s) => ({ ...s, county: e.target.value }))} data-testid="cust-county" placeholder="Auto-filled from ZIP" />
            </div>
          </div>

          <div
            className={`rounded-md border p-3 mt-4 ${smsConsent ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}
            data-testid="sms-consent-block"
          >
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-400 text-blue-700 focus:ring-blue-600 cursor-pointer"
                data-testid="cust-sms-consent"
              />
              <div className="flex-1 space-y-2">
                <div className="text-sm font-semibold text-slate-800">
                  SMS opt-in on file
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Check this box <strong>only if the customer has given you permission</strong>{" "}
                  to send them SMS updates (via the booking form, or verbally / in writing).
                  Required by A2P&nbsp;10DLC — you can't text customers who haven't opted in.
                </p>
                {smsConsent && (
                  <div className="flex items-center gap-2 pt-1">
                    <Label className="text-xs text-slate-700 shrink-0">How they opted in:</Label>
                    <select
                      value={smsConsentSource}
                      onChange={(e) => setSmsConsentSource(e.target.value)}
                      className="text-xs border border-slate-300 rounded-md px-2 py-1 bg-white"
                      data-testid="cust-sms-consent-source"
                    >
                      <option value="verbal">Verbal (on the phone)</option>
                      <option value="written">Written (email or paper)</option>
                      <option value="public_booking_form">Via /book form</option>
                      <option value="admin_manual">Admin-recorded</option>
                    </select>
                  </div>
                )}
                {smsConsent && initialConsentAt && (
                  <div className="text-[11px] text-emerald-800">
                    On file since {new Date(initialConsentAt).toLocaleString()}
                  </div>
                )}
              </div>
            </label>
          </div>

          {dupMatch?.match && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 mt-4 space-y-2" data-testid="dup-warning">
              <div className="text-sm font-semibold text-amber-900">
                A customer with this {dupMatch.matched_by} already exists
              </div>
              <div className="text-sm text-amber-800">
                {dupMatch.match.first_name} {dupMatch.match.last_name} · {dupMatch.match.phone}
                {dupMatch.match.email ? ` · ${dupMatch.match.email}` : ""}
              </div>
              <div className="flex gap-2 pt-1">
                <Button size="sm" onClick={() => { setDupMatch(null); openEdit(dupMatch.match); }} className="bg-blue-700 hover:bg-blue-800" data-testid="dup-use-existing">
                  Use existing customer
                </Button>
                <Button size="sm" variant="outline" onClick={() => save(true)} data-testid="dup-create-anyway">
                  Create anyway
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => save()} className="bg-blue-700 hover:bg-blue-800" data-testid="cust-save">{editing ? "Save changes" : "Add customer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="job-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-4 h-4 text-blue-700" />
              Job history — {historyFor?.first_name} {historyFor?.last_name}
            </DialogTitle>
          </DialogHeader>
          {historyFor && <JobHistory customerId={historyFor.id} />}
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={(o) => (o ? setImportOpen(true) : closeImport())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import customers from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-slate-600 space-y-2">
              <p>Upload a CSV with these columns (header row required):</p>
              <code className="block text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2 font-mono">
                first_name, last_name, email, phone, street, city, state, zip_code
              </code>
              <p className="text-xs text-slate-500">
                <strong>first_name, last_name, phone</strong> are required. Other columns are optional. Rows missing required fields will be skipped.
              </p>
              <Button type="button" variant="link" className="h-auto p-0 gap-1 text-blue-700" onClick={downloadTemplate} data-testid="download-template">
                <Download className="w-3.5 h-3.5" /> Download sample template
              </Button>
            </div>

            <div className="space-y-2">
              <Label>CSV file</Label>
              <Input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                data-testid="import-file-input"
              />
            </div>

            {importResult && (
              <div className="text-sm bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1" data-testid="import-result">
                <div className="text-green-700 font-medium">Imported: {importResult.created} of {importResult.total_rows}</div>
                {importResult.skipped?.length > 0 && (
                  <div className="text-amber-700">
                    Skipped {importResult.skipped.length} row(s):
                    <ul className="list-disc ml-5 mt-1 text-xs">
                      {importResult.skipped.slice(0, 5).map((s, i) => (
                        <li key={i}>Row {s.row} — {s.reason}</li>
                      ))}
                      {importResult.skipped.length > 5 && <li>…and {importResult.skipped.length - 5} more</li>}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeImport}>Close</Button>
            <Button onClick={runImport} disabled={importing || !importFile} className="bg-blue-700 hover:bg-blue-800" data-testid="run-import">
              {importing ? "Importing…" : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
